import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import folderService from 'v2/services/folderService';

import legacyFolderService from '../../../services/folderService';
import { ScribeFolder } from '../../components/navigation/SideBar/Folders/FolderTree/FolderTree';
import { FolderTree, RootState } from '../../types';

export const selectFolderId = createAction<string | null>('folders/selectFolderId');
export const openFolder = createAction<ScribeFolder>('folders/openFolder');

export const fetchFoldersForTeam = createAsyncThunk('folders/fetchTree', async ({ teamId }: { teamId: string }) => {
  const fetchPrivateFolders = false;
  const response = await folderService.fetch({ teamId, isPrivate: fetchPrivateFolders });

  return response as Array<FolderTree>;
});

export const fetchPrivateFolders = createAsyncThunk('folders/fetchPrivateTree', async ({ teamId }: { teamId: string }) => {
  const fetchPrivateFolders = true;
  const response = await folderService.fetch({ teamId, isPrivate: fetchPrivateFolders });

  return response as Array<FolderTree>;
});

export const createFolder = createAsyncThunk(
  'folders/createFolder',
  async ({ body }: { body: { name: string; parent_id?: string; is_private: boolean; organization_id?: string } }) => {
    const conditionalBody = body.is_private ? body : { name: body.name, parent_id: body?.parent_id, organization_id: body.organization_id };
    const response = await legacyFolderService.createFolder(conditionalBody);

    return response as ScribeFolder;
  }
);

export const editFolder = createAsyncThunk(
  'folders/editFolder',
  async ({ id, body }: { id: string; body: { name: string } }, { getState }: { getState: RootState }) => {
    const state = getState();
    const folderIsPrivate: boolean = JSON.stringify(state.folders.privateFolders).includes(id);
    const response = await legacyFolderService.editFolder(id, body);

    return { response: response as ScribeFolder, folderIsPrivate };
  }
);

export const deleteFolder = createAsyncThunk('folders/deleteFolder', async (id: string, { getState }: { getState: RootState }) => {
  const state = getState();
  const folderIsPrivate: boolean = JSON.stringify(state.folders.privateFolders).includes(id);

  await legacyFolderService.deleteFolder(id);
  return { id, folderIsPrivate };
});

type ValidationError = [string];

const unknownValidationError: ValidationError = ['Something went wrong.'];

function isValidationError(error: unknown): error is ValidationError {
  return error[0] !== undefined && typeof error[0] === 'string';
}

export const moveFolder = createAsyncThunk<boolean, { folder: ScribeFolder; parentId: string | null }, { rejectValue: ValidationError }>(
  'folders/moveFolder',
  async ({ folder, parentId }, { rejectWithValue }) => {
    try {
      await folderService.move(folder.id, parentId);
      const folderIsPrivate = folder.organization_permission === null;

      return folderIsPrivate;
    } catch (e) {
      console.warn(e);
      if (isValidationError(e)) {
        return rejectWithValue(e);
      }
      return rejectWithValue(unknownValidationError);
    }
  }
);
